import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import fetchSearchCategoriesData from "@DSG/Services/DSGSearchCategoryService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {};
};

export interface DSGTopCategoriesStyles {
    topCategoriesSection: InjectableCss;
    sectionTitle: TypographyPresentationProps;
    wrapperContainer: GridContainerProps;
    categoryItem: GridItemProps;
    categoryTitle: TypographyPresentationProps;
    clickable: ClickablePresentationProps;
    searchHelpWrapper: InjectableCss;
    noResultsFullWidthHeading: InjectableCss;
}

export const topCategoriesStyles: DSGTopCategoriesStyles = {
    topCategoriesSection: {
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 40px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    sectionTitle: {
        size: bodyFonts.body1SemiBoldLg.fontSize,
        lineHeight: bodyFonts.body1SemiBoldLg.lineHeight,
        weight: bodyFonts.body1SemiBoldLg.fontWeight,
        css: css`
            margin-bottom: 16px;
        `,
    },
    wrapperContainer: {
        offsetCss: css`
            gap: 16px;
            width: 100%;
            margin: 0;
        `,
        css: css`
            > ${GridOffset} {
                width: 100%;
                justify-content: flex-start;
                align-items: stretch;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                gap: 24px;
                            `,
                            null,
                        ],
                        "min",
                    )}

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        justify-content: center;
                    `,
                    null,
                    null,
                ], "max")}
            }
            ${GridItemStyle} {
                justify-content: center;
                text-align: center;
                border: 1px solid ${colorPallete.grey500};
                border-radius: 8px;
                padding: 16px;
                max-width: 163px !important;
                flex-basis: 163px !important;
                @media only screen and (min-width: 540px) {
                    max-width: 185px !important;
                    flex-basis: 185px !important;
                }
                &:hover {
                    border-color: ${colorPallete.grey800};
                    box-shadow: ${elevation.dp04};
                }
            }
        `,
    },
    categoryItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            [class*="LazyImageStyle"] {
                padding: 0 !important;
            }
        `,
    },
    categoryTitle: {
        size: labelFonts.label1SemiBold.fontSize,
        lineHeight: labelFonts.label1SemiBold.lineHeight,
        weight: labelFonts.label1SemiBold.fontWeight,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${labelFonts.label1SemiBold.fontSize};
                            line-height: ${labelFonts.label1SemiBold.lineHeight};
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    clickable: {
        css: css`
            flex-direction: column;
            img {
                margin-bottom: 8px;
                max-width: 153px;
                min-height: 97px;
                max-height: 97px;
                object-fit: cover;
            }
        `,
    },
    searchHelpWrapper: {
        css: css`
            padding: 0 40px 40px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0 0 40px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}

            .search-help-container {
                display: flex;
                align-items: center;
                padding: 16px 24px;
                background: ${colorPallete.grey100};
                border-radius: 8px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 16px;
                                flex-direction: column;
                                align-items: flex-start;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}

                p {
                    font-size: ${headlineFonts.headline6SemiBoldLg.fontSize};
                    line-height: ${headlineFonts.headline6SemiBoldLg.lineHeight};
                    font-weight: ${headlineFonts.headline6SemiBoldLg.fontWeight};
                    padding-right: 8px;
                    flex-shrink: 0;
                }

                .search-help-links {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    flex-direction: column;
                                    align-items: flex-start;
                                    margin-top: 16px;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}

                    li {
                        a {
                            display: inline-block;
                            padding: 0 8px;
                            font-size: ${labelFonts.label2SemiBold.fontSize};
                            line-height: ${labelFonts.label2SemiBold.lineHeight};
                            font-weight: ${labelFonts.label2SemiBold.fontWeight};
                            color: ${colorPallete.primaryRed500};
                            border-left: 1px solid ${colorPallete.grey400};

                            &:hover {
                                color: ${colorPallete.primaryMaroon500};
                            }

                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            padding: 0;
                                            border: none;
                                        `,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )}
                        }

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        line-height: ${labelFonts.label2SemiBold.lineHeight};
                                        &:not(:last-child) {
                                            padding: 0 0 8px;
                                        }
                                    `,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                }
            }
        `,
    },
    noResultsFullWidthHeading: {
        css: css`
            font: ${headlineFonts.headline4SemiBoldSm.fontWeight} ${headlineFonts.headline4SemiBoldSm.fontSize} /
                ${labelFonts.label2SemiBold.lineHeight} ${headlineFonts.headline4SemiBoldSm.fontFamily};
            margin-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            font-size: ${headlineFonts.headline4SemiBoldLg.fontSize};
                            line-height: ${headlineFonts.headline4SemiBoldLg.lineHeight};
                        `,
                    ],
                    "min",
                )}
        `,
    }
};

const DSGSearchCategories = () => {
    const styles = topCategoriesStyles;
    const [items, setItems] = React.useState<any[]>([]);
    const [error, setError] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        const fetchCategoryData = async () => {
            try {
                const response = await fetchSearchCategoriesData();
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = (await response.json()) as any;
                if (data) {
                    setItems(data?.searchCategoryList);
                    data?.searchCategoryList?.length > 0 && setIsLoading(true);
                }
            } catch (error) {
                setError(error);
                setIsLoading(false);
            }
        };
        fetchCategoryData();
    }, []);

    return (
        <StyledWrapper {...styles.topCategoriesSection} className="dsg-search-categories-section">
            <Typography as="h3" {...styles.sectionTitle}>
                {translate("Top Categories")}
            </Typography>
            <GridContainer {...styles.wrapperContainer} className="search-categories-wrapper">
                <>
                    {items && items.length > 0 ? (
                        items.map(
                            (item: {
                                smallImagePath: string | undefined;
                                imageAltText: string | undefined;
                                name: string | undefined;
                                urlSegment: string | undefined;
                            }) => (
                                <GridItem key={`${item.name}_id`} {...styles.categoryItem}>
                                    <Clickable href={item.urlSegment} {...styles.clickable}>
                                        {item.smallImagePath && (
                                            <LazyImage src={item.smallImagePath} altText={item.imageAltText} />
                                        )}
                                        <Typography {...styles.categoryTitle}>{item.name || "Default Name"}</Typography>
                                    </Clickable>
                                </GridItem>
                            ),
                        ) // <- Add closing parenthesis for map function call here
                    ) : isLoading ? (
                        <LoadingSpinner color="primary" size={15} />
                    ) : (
                        <StyledWrapper {...styles.noResultsFullWidthHeading}>No items to display</StyledWrapper>
                    )}
                </>
            </GridContainer>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, null)(DSGSearchCategories);
